import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { ArrowLeft, CircleNotch, Target, Lightning, Flame, Timer, ArrowsInLineHorizontal, CalendarBlank } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { Window6Content } from './Window6MonthlyFrequency';
import { Window11Content } from './Window11PositionalHotCold';
import { Window21Content } from './Window21DecayFrequency';
import { Window13Content } from './Window13GapAnalysis';
import { Window8Content } from './Window8BeforeAfter';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const statusColors = {
  HOT: 'bg-red-100 text-red-800',
  WARM: 'bg-amber-100 text-amber-800',
  COLD: 'bg-blue-100 text-blue-800',
  HEATING: 'bg-orange-100 text-orange-800',
  COOLING: 'bg-cyan-100 text-cyan-800',
  FLAT: 'bg-gray-100 text-gray-600',
  OVERDUE: 'bg-red-100 text-red-800',
  DUE: 'bg-amber-100 text-amber-800',
  NORMAL: 'bg-green-100 text-green-800',
};

function WindowTopCard({ label, icon, color, top5 }) {
  return (
    <div className={`rounded-lg border p-3 ${color}`} data-testid={`window-card-${label}`}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-xs font-bold">{label}</span>
      </div>
      <div className="space-y-1">
        {top5.map((n, i) => (
          <div key={i} className="flex items-center justify-between text-[10px]">
            <span className="font-mono font-bold">#{n.number}</span>
            <div className="flex items-center gap-1.5">
              {n.status && <span className={`px-1 py-0.5 rounded text-[9px] font-bold ${statusColors[n.status] || 'bg-gray-100'}`}>{n.status}</span>}
              <span className="font-mono">{n.score}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


/**
 * 🎯 Ball Master — unified per-ball probability aggregator.
 *
 * Renders a 5-column grid (Ball 1..5). Each column shows the top-10 candidates
 * ranked by the fused score = Σ(window_weight · appears_in_top5) + W37 tier bonus.
 *
 * The top pick per ball gets a big oversized display (source-of-truth
 * candidate). Below it the remaining 9 slots are compact list-rows with tier
 * chips + a click-to-expand provenance list showing which windows voted for
 * that number.
 *
 * Data source: GET /api/analysis/w53-ball-master.
 */
const TIER_STYLE = {
  overdue: 'bg-rose-700 text-white ring-2 ring-amber-400',
  due: 'bg-rose-500 text-white',
  approaching: 'bg-amber-400 text-amber-900',
};

function BallMasterBlock({ data, loading, ballLabels, ballKeys, expandedMap, setExpanded, navigate }) {
  if (loading) {
    return (
      <div className="rounded-2xl border-2 border-fuchsia-400 bg-gradient-to-br from-fuchsia-50 via-rose-50 to-amber-50 shadow-md overflow-hidden mb-6" data-testid="ball-master-loading">
        <div className="px-5 py-3 bg-gradient-to-r from-fuchsia-700 via-rose-600 to-orange-500 text-white flex items-center gap-3">
          <Target size={20} weight="fill" className="text-amber-300" />
          <h3 className="text-sm font-black uppercase tracking-wider">🎯 Ball Master — Unified Aggregator</h3>
        </div>
        <div className="px-5 py-8 flex items-center justify-center text-fuchsia-800 text-xs">
          <CircleNotch size={18} className="animate-spin mr-2" />
          Fusing scorecards…
        </div>
      </div>
    );
  }
  if (!data || !data.balls) return null;

  const toggleExpand = (bk, number) => {
    const key = `${bk}-${number}`;
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const testDraws = data.source_test_draws;

  return (
    <div className="rounded-2xl border-2 border-fuchsia-400 bg-gradient-to-br from-fuchsia-50 via-rose-50 to-amber-50 shadow-md overflow-hidden mb-6" data-testid="ball-master-block">
      <div className="px-5 py-3 bg-gradient-to-r from-fuchsia-700 via-rose-600 to-orange-500 text-white flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Target size={22} weight="fill" className="text-amber-300" />
          <div>
            <h3 className="text-sm font-black uppercase tracking-wider">🎯 Ball Master — Unified Aggregator</h3>
            <p className="text-[11px] text-white/85 italic">
              Every scorecard fused into one per-ball probability · weighted by W50 top-5 hit rate ·
              W37 tier bonus stacked on top {testDraws ? `· backtested on ${testDraws} draws` : ''}
            </p>
          </div>
        </div>
        <span className="text-[10px] font-mono bg-white/20 rounded px-2 py-1">
          {(data.weights || []).length} scorecards
        </span>
      </div>

      <div className="p-4 space-y-4">
        {/* Top pick per ball — a "recommended ticket" strip. */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="ball-master-top-strip">
          {ballKeys.map((bk, idx) => {
            const top = (data.balls[bk]?.top_10 || [])[0];
            if (!top) {
              return (
                <div key={bk} className="rounded-xl bg-white/60 border border-fuchsia-200 p-3 text-center text-fuchsia-400 text-xs italic">
                  {ballLabels[bk]} — no data
                </div>
              );
            }
            const tierCls = top.tier_bonus > 0
              ? (top.tier_bonus >= 0.30 ? TIER_STYLE.overdue : top.tier_bonus >= 0.20 ? TIER_STYLE.due : TIER_STYLE.approaching)
              : 'bg-gradient-to-br from-fuchsia-700 to-rose-600 text-white';
            return (
              <div
                key={bk}
                className="rounded-xl bg-white border-2 border-fuchsia-300 p-3 text-center shadow-sm hover:shadow-md transition-shadow"
                data-testid={`ball-master-top-${bk}`}
              >
                <p className="text-[10px] uppercase tracking-wider font-black text-fuchsia-700">{ballLabels[bk]}</p>
                <span className={`inline-flex w-14 h-14 rounded-full items-center justify-center font-mono font-black text-2xl mt-1.5 ${tierCls}`}>
                  {top.number}
                </span>
                <p className="text-[11px] font-bold text-fuchsia-900 mt-1.5">Score {top.score}</p>
                <p className="text-[9px] text-fuchsia-500 font-mono">
                  {top.vote_count} votes{top.tier_bonus > 0 ? ` · +${top.tier_bonus} bonus` : ''}
                </p>
              </div>
            );
          })}
        </div>

        {/* Per-ball top-10 detail cards. */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
          {ballKeys.map((bk, bidx) => {
            const top10 = (data.balls[bk]?.top_10 || []);
            return (
              <div key={bk} className="rounded-xl bg-white border border-fuchsia-200 overflow-hidden" data-testid={`ball-master-col-${bk}`}>
                <div className="px-3 py-2 bg-fuchsia-100 text-fuchsia-900 flex items-center justify-between">
                  <span className="text-xs font-black uppercase tracking-wider">{ballLabels[bk]}</span>
                  <span className="text-[10px] font-mono text-fuchsia-500">{top10.length}</span>
                </div>
                <div className="p-2 space-y-1">
                  {top10.length === 0 ? (
                    <p className="text-[10px] text-fuchsia-300 italic text-center py-3">No signal</p>
                  ) : top10.map((r, ri) => {
                    const expanded = !!expandedMap[`${bk}-${r.number}`];
                    const tierCls = r.tier_bonus >= 0.30 ? TIER_STYLE.overdue
                      : r.tier_bonus >= 0.20 ? TIER_STYLE.due
                      : r.tier_bonus >= 0.10 ? TIER_STYLE.approaching
                      : 'bg-fuchsia-600 text-white';
                    return (
                      <div key={r.number} className="rounded border border-fuchsia-100 hover:border-fuchsia-300 transition-colors" data-testid={`ball-master-row-${bk}-${r.number}`}>
                        <button
                          type="button"
                          onClick={() => toggleExpand(bk, r.number)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 text-left"
                          data-testid={`ball-master-toggle-${bk}-${r.number}`}
                        >
                          <span className="text-[9px] font-mono text-fuchsia-400 w-4">#{ri + 1}</span>
                          <span className={`inline-flex w-8 h-8 rounded-full items-center justify-center font-mono font-black text-xs ${tierCls}`}>
                            {r.number}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-[11px] font-bold text-fuchsia-900">Score {r.score}</div>
                            <div className="text-[9px] text-fuchsia-500 font-mono">
                              {r.vote_count}× votes{r.tier_bonus > 0 ? ` · +${r.tier_bonus}` : ''}
                            </div>
                          </div>
                          <span className={`text-fuchsia-400 text-[10px] transition-transform ${expanded ? 'rotate-90' : ''}`}>▶</span>
                        </button>
                        {expanded && (
                          <div className="border-t border-fuchsia-100 bg-fuchsia-50/60 px-2 py-1.5" data-testid={`ball-master-provenance-${bk}-${r.number}`}>
                            <p className="text-[9px] font-black uppercase tracking-wider text-fuchsia-700 mb-1">Voted by</p>
                            <div className="flex flex-wrap gap-1">
                              {r.votes_from.map((v, vi) => (
                                <button
                                  key={`${v.window_key}-${vi}`}
                                  type="button"
                                  onClick={() => v.path && navigate(v.path)}
                                  className="inline-flex items-center gap-1 rounded bg-white border border-fuchsia-200 px-1.5 py-0.5 hover:border-fuchsia-500 transition-colors"
                                  title={`${v.window_name || v.window_key} · rank ${v.rank}${v.tier ? ` · ${v.tier}` : ''}${v.raw_score != null ? ` · raw ${v.raw_score}` : ''}`}
                                >
                                  <span className="text-[9px] font-mono font-bold text-fuchsia-700">{v.window_key}</span>
                                  <span className="text-[8px] text-fuchsia-400">#{v.rank}</span>
                                  {v.tier && (
                                    <span className={`text-[8px] font-black uppercase ${v.tier === 'overdue' ? 'text-rose-700' : v.tier === 'due' ? 'text-rose-500' : 'text-amber-600'}`}>
                                      {v.tier.charAt(0)}
                                    </span>
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Weights breakdown */}
        {(data.weights || []).length > 0 && (
          <details className="rounded-lg bg-white/70 border border-fuchsia-200" data-testid="ball-master-weights">
            <summary className="cursor-pointer px-3 py-2 text-[11px] font-black uppercase tracking-wider text-fuchsia-800 hover:bg-white/90">
              How is this scored? ({(data.weights || []).length} windows · click to expand)
            </summary>
            <div className="px-3 py-2 border-t border-fuchsia-200">
              <p className="text-[11px] text-fuchsia-700 mb-2 leading-relaxed">
                For each candidate <span className="font-mono font-bold">n</span> we sum weighted votes from every scorecard whose top-5 for the ball includes <span className="font-mono font-bold">n</span>.
                Each window&apos;s weight = its empirical <b>W50 top-5 hit rate</b> (with a {data.weight_floor_pct}% floor for un-tracked windows). W37 poses currently DUE / OVERDUE / APPROACHING add a tier bonus.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-1.5 text-[10px] font-mono">
                {(data.weights || []).map(w => (
                  <div key={w.window_key} className="flex items-center justify-between rounded bg-fuchsia-50 border border-fuchsia-100 px-2 py-1">
                    <span className="font-bold text-fuchsia-800">{w.window_key}</span>
                    <span className="text-fuchsia-600">{w.weight}</span>
                    {w.top5_hit_rate != null && <span className="text-fuchsia-400 text-[9px]">({w.top5_hit_rate}%)</span>}
                  </div>
                ))}
              </div>
            </div>
          </details>
        )}
      </div>
    </div>
  );
}


export default function MasterPrediction() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [connData, setConnData] = useState(null);
  const [sigData, setSigData] = useState(null);
  const [dateSignal, setDateSignal] = useState(null);
  const [ballMaster, setBallMaster] = useState(null);
  const [ballMasterLoading, setBallMasterLoading] = useState(true);
  const [ballMasterExpanded, setBallMasterExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedBall, setSelectedBall] = useState('ball_1');
  const [windowTab, setWindowTab] = useState('consensus');

  useEffect(() => {
    axios.get(`${API}/analysis/master-prediction`)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => { toast.error('Failed to load master prediction'); setLoading(false); });
    axios.get(`${API}/analysis/mirror-complement`)
      .then(res => setConnData(res.data))
      .catch(() => { /* mirror-complement optional */ });
    axios.get(`${API}/analysis/signals`)
      .then(res => setSigData(res.data))
      .catch(() => { /* signals optional */ });
    axios.get(`${API}/analysis/date-pattern-signal`)
      .then(res => setDateSignal(res.data))
      .catch(() => { /* date-pattern optional */ });
    axios.get(`${API}/analysis/w53-ball-master`)
      .then(res => { setBallMaster(res.data); setBallMasterLoading(false); })
      .catch(() => { setBallMasterLoading(false); });
  }, []);

  const ballLabels = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };
  const ballKeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];

  // Memoise expensive filter operations used in JSX render
  const strongDateSignals = useMemo(
    () => (dateSignal?.date_signals || []).filter(s => s.strength === 'VERY HIGH' || s.strength === 'HIGH'),
    [dateSignal]
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-[1600px] mx-auto flex items-center gap-4">
          <button onClick={() => navigate('/')} className="p-2 rounded-lg hover:bg-gray-100" data-testid="back-btn"><ArrowLeft size={20} /></button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">W53: Number Prediction: B1 to B5</h1>
            <p className="text-xs text-gray-500">Consensus prediction: W1-W14 per-ball scoring + Indicators I1 (LS Digit), I2 (Tier Deficit), I3 (Recency Tier), I4 (DG Date Convergence)</p>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">


        <WindowNarrative windowId="W53" {...NARRATIVES.W53} className="mb-5" />

        {/* ================== 🎯 BALL MASTER AGGREGATOR ================== */}
        {/* Unified per-ball probability fusing every scorecard in the app: all
            ~21 W41 per-window predictions weighted by their W50 empirical
            top-5 hit rate + W37 tier bonuses (OVERDUE / DUE / APPROACHING).
            Backed by /api/analysis/w53-ball-master. */}
        <BallMasterBlock
          data={ballMaster}
          loading={ballMasterLoading}
          ballLabels={ballLabels}
          ballKeys={ballKeys}
          expandedMap={ballMasterExpanded}
          setExpanded={setBallMasterExpanded}
          navigate={navigate}
        />
        {/* =============================================================== */}

        {/* Window Tabs */}
        <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1 mb-6 flex-wrap" data-testid="window-tabs">
          {[
            { key: 'consensus', label: 'Consensus', color: 'bg-[#002FA7]' },
            { key: 'w6', label: 'W6: Monthly', color: 'bg-purple-600' },
            { key: 'w11', label: 'W11: Hot/Cold', color: 'bg-red-600' },
            { key: 'w21', label: 'W21: Momentum', color: 'bg-orange-600' },
            { key: 'w13', label: 'W13: Gap', color: 'bg-blue-600' },
            { key: 'w8', label: 'W8: Before/After', color: 'bg-green-600' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setWindowTab(t.key)}
              className={`px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${
                windowTab === t.key ? `${t.color} text-white` : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid={`wtab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Embedded Window Content */}
        {windowTab === 'w6' && <Window6Content />}
        {windowTab === 'w11' && <Window11Content />}
        {windowTab === 'w21' && <Window21Content />}
        {windowTab === 'w13' && <Window13Content />}
        {windowTab === 'w8' && <Window8Content />}

        {/* Consensus Tab (original content) */}
        {windowTab === 'consensus' && (
          loading ? (
          <div className="flex items-center justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>
        ) : !data ? (
          <div className="text-center py-10 text-gray-500">Failed to load data</div>
        ) : (
          <div className="space-y-6">
            {/* Summary: Top consensus pick per ball */}
            <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="consensus-summary">
              <h2 className="text-sm font-bold text-gray-900 mb-1">Consensus Top Pick Per Ball</h2>
              <p className="text-[10px] text-gray-400 mb-4">Last draw #{data.last_draw?.draw_number} ({data.last_draw?.draw_date}). Primary: W1-W13 weighted scoring. Supplementary: W11 Hot/Cold, W21 Momentum, W13 Gap, W8 Before/After</p>
              <div className="grid grid-cols-5 gap-4">
                {ballKeys.map((bk) => {
                  const bd = data.balls?.[bk];
                  if (!bd) return null;
                  const top = bd.top_10?.[0];
                  if (!top) return null;
                  return (
                    <div key={bk} className="text-center bg-gradient-to-b from-[#002FA7] to-[#001d6a] text-white rounded-xl p-4" data-testid={`summary-${bk}`}>
                      <p className="text-[10px] text-white/60 uppercase font-bold">{ballLabels[bk]}</p>
                      <p className="text-[10px] text-white/40">Current: {bd.current_number}</p>
                      <p className="text-4xl font-black mt-2">{top.number}</p>
                      <p className="text-xs text-white/70 mt-1">Score: {top.consensus_score || top.w3_score}</p>
                      <p className="text-[10px] text-white/50">{top.agreement}/9 signals | {top.w11_status}{top.indicators?.total > 0 ? ` | +${top.indicators.total} ind` : ''}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Date Pattern Signal — Compact for Master Prediction */}
            {dateSignal?.date_signals?.filter(s => s.strength === 'VERY HIGH' || s.strength === 'HIGH').length > 0 && (
              <div className="bg-white border-2 border-orange-400 rounded-lg p-4" data-testid="date-pattern-master">
                <div className="flex items-center gap-2 mb-3">
                  <CalendarBlank size={18} weight="fill" className="text-orange-600" />
                  <h3 className="text-sm font-bold text-gray-900">Date Pattern Signal — Next Draws</h3>
                </div>
                <div className="space-y-3">
                  {strongDateSignals.map((sig, i) => (
                    <div key={`ds-${sig.target_date?.date_str || i}`} className={`rounded-lg border p-3 ${sig.strength === 'VERY HIGH' ? 'border-red-300 bg-red-50' : 'border-orange-200 bg-orange-50'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-black ${sig.strength === 'VERY HIGH' ? 'bg-red-600 text-white' : 'bg-orange-500 text-white'}`}>{sig.strength}</span>
                        <span className="text-xs font-bold text-gray-800">{sig.target_date.date_str} ({sig.target_date.weekday})</span>
                        <span className="text-[10px] text-gray-400">{sig.exact_match_draws} historical draws</span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {sig.hot_numbers.filter(h => h.date_pct >= 30).map(h => (
                          <div key={h.number} className="flex items-center gap-1">
                            <span className={`inline-flex w-8 h-8 rounded-full items-center justify-center font-mono font-bold text-sm text-white ${h.date_pct >= 50 ? 'bg-red-600' : 'bg-amber-500'}`}>{h.number}</span>
                            <span className="text-[10px] font-bold text-gray-600">{h.date_count}/{h.date_appearances}</span>
                          </div>
                        ))}
                        {sig.recurring_pairs?.length > 0 && (
                          <span className="text-[10px] font-bold text-red-700 bg-red-100 px-1.5 py-0.5 rounded ml-2">
                            Pair [{sig.recurring_pairs[0].pair.join(',')}] {sig.recurring_pairs[0].count}x ({sig.recurring_pairs[0].pct}%)
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Missing Digit Signal */}
            {data.indicators_active && (
              <div className="bg-white border border-rose-200 rounded-lg p-4" data-testid="missing-digit-signal">
                <h3 className="text-sm font-bold text-gray-900 mb-1">Missing Digit Signal — {data.current_year}</h3>
                <p className="text-[10px] text-gray-400 mb-3">Digits (0-9) completely missing at each position in {data.current_year}. Numbers ending in these digits are overdue.</p>
                <div className="grid grid-cols-5 gap-3">
                  {ballKeys.map((bk, bi) => {
                    const allNums = data.balls?.[bk]?.all_numbers || [];
                    const digitMap = {};
                    allNums.forEach(n => {
                      const dg = n.number % 10;
                      if (!digitMap[dg]) digitMap[dg] = [];
                      digitMap[dg].push(n.number);
                    });
                    // Compute missing from the indicators (from backend digit_signals if available)
                    // For now derive from the data
                    return (
                      <div key={bk} className="text-center">
                        <p className="text-[10px] font-bold text-gray-500 mb-1">Ball {bi + 1}</p>
                        <div className="flex gap-1 justify-center flex-wrap">
                          {(() => {
                            // Find digits with 0 appearances in 2026 at this position
                            const cyCount = {};
                            for (let d = 0; d < 10; d++) cyCount[d] = 0;
                            allNums.forEach(n => {
                              if (n.cy_count > 0) cyCount[n.number % 10] += n.cy_count;
                            });
                            const missing = [];
                            for (let d = 0; d < 10; d++) {
                              if (cyCount[d] === 0) missing.push(d);
                            }
                            if (missing.length === 0) return <span className="text-[10px] text-gray-400">All present</span>;
                            return missing.map(d => (
                              <span key={d} className="inline-flex w-7 h-7 rounded-full bg-red-600 text-white items-center justify-center font-mono font-bold text-xs">{d}</span>
                            ));
                          })()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Number Connection Signal */}
            {connData?.connections?.active_signals && Object.keys(connData.connections.active_signals).length > 0 && (
              <div className="bg-white border border-amber-200 rounded-lg p-4" data-testid="connection-signal">
                <h3 className="text-sm font-bold text-gray-900 mb-1">Number Connection Signal — {connData.current_year}</h3>
                <p className="text-[10px] text-gray-400 mb-3">Last draw [{connData.last_draw?.balls?.join(', ')}] — partners most likely to follow based on {connData.current_year} co-occurrence</p>
                <div className="grid grid-cols-5 gap-3">
                  {(connData.last_draw?.balls || []).map(num => {
                    const partners = connData.connections.active_signals?.[num] || [];
                    return (
                      <div key={num} className="text-center">
                        <p className="text-[10px] font-bold text-gray-500 mb-1">
                          <span className="inline-flex w-7 h-7 rounded-full bg-amber-500 text-white items-center justify-center font-mono font-bold text-xs">{num}</span>
                        </p>
                        <div className="flex gap-0.5 justify-center flex-wrap">
                          {partners.length > 0 ? partners.slice(0, 4).map(p => (
                            <span key={p.number} className="text-[9px] font-mono font-bold bg-amber-100 text-amber-800 px-1 py-0.5 rounded">{p.number}</span>
                          )) : <span className="text-[9px] text-gray-400">-</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Pair Shift Signal — VERY HIGH */}
            {sigData?.pair_shift_signal?.predictions?.length > 0 && (
              <div className="bg-white border-2 border-red-400 rounded-lg p-4" data-testid="pair-shift-master">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2 py-0.5 rounded text-[10px] font-black bg-red-600 text-white">VERY HIGH SIGNAL</span>
                  <h3 className="text-sm font-bold text-gray-900">Consecutive Pair Shift Prediction</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {sigData.pair_shift_signal.predictions.slice(0, 6).map((pred, i) => (
                    <div key={i} className={`rounded-lg border-2 px-3 py-2 ${pred.decade_missing ? 'border-red-400 bg-red-50' : 'border-amber-300 bg-amber-50'}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="inline-flex w-9 h-9 rounded-full bg-red-600 text-white items-center justify-center font-mono font-bold">{pred.predicted_pair[0]}</span>
                        <span className="inline-flex w-9 h-9 rounded-full bg-red-600 text-white items-center justify-center font-mono font-bold">{pred.predicted_pair[1]}</span>
                        <div>
                          {pred.decade_missing && <p className="text-[10px] font-bold text-red-700">Decade {pred.predicted_decade} MISSING</p>}
                          <p className="text-[9px] text-gray-400">{pred.proof_paths?.length || 0} proof paths</p>
                        </div>
                      </div>
                      {(pred.proof_paths || []).slice(0, 3).map((pp, pi) => (
                        <div key={pi} className="text-[10px] text-gray-600">
                          <span className="font-mono font-bold">{pp.from_pair[0]},{pp.from_pair[1]}</span>
                          <span className={`mx-0.5 font-bold ${pp.shift < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{pp.shift > 0 ? '+' : ''}{pp.shift}</span>
                          {pp.proven_by && <span className="text-[9px] text-gray-400 ml-0.5">({pp.proven_by.split(' | ')[0]})</span>}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ball selector tabs */}
            <div className="flex gap-2 border-b border-gray-200 pb-0" data-testid="ball-selector">
              {ballKeys.map((bk) => (
                <button
                  key={bk}
                  onClick={() => setSelectedBall(bk)}
                  className={`px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-[1px] ${
                    selectedBall === bk
                      ? 'border-[#002FA7] text-[#002FA7]'
                      : 'border-transparent text-gray-400 hover:text-gray-600'
                  }`}
                  data-testid={`tab-${bk}`}
                >
                  {ballLabels[bk]}
                </button>
              ))}
            </div>

            {/* Selected ball detail */}
            {(() => {
              const bd = data.balls?.[selectedBall];
              if (!bd) return null;
              const wt = bd.window_tops || {};

              return (
                <div className="space-y-4" data-testid={`detail-${selectedBall}`}>
                  {/* 5 Window Cards side by side */}
                  <div className="grid grid-cols-5 gap-3">
                    <WindowTopCard label="W3: Per-Ball (W1-W13)" icon={<Target size={14} className="text-purple-700" />} color="border-purple-200 bg-purple-50/30" top5={wt.w3 || []} />
                    <WindowTopCard label="W11: Hot/Cold" icon={<Flame size={14} className="text-red-700" />} color="border-red-200 bg-red-50/30" top5={wt.w11 || []} />
                    <WindowTopCard label="W21: Momentum" icon={<Lightning size={14} className="text-orange-700" />} color="border-orange-200 bg-orange-50/30" top5={wt.w21 || []} />
                    <WindowTopCard label="W13: Gap/Overdue" icon={<Timer size={14} className="text-blue-700" />} color="border-blue-200 bg-blue-50/30" top5={wt.w13 || []} />
                    <WindowTopCard label="W8: Before/After" icon={<ArrowsInLineHorizontal size={14} className="text-green-700" />} color="border-green-200 bg-green-50/30" top5={wt.w8 || []} />
                  </div>

                  {/* Top 10 Consensus Table */}
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="font-bold text-gray-900">{ballLabels[selectedBall]} — Top 10 Consensus</h3>
                      <p className="text-[10px] text-gray-400 mt-0.5">Current number: {bd.current_number}</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs" data-testid="consensus-table">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-3 py-2 text-left font-bold text-gray-500">#</th>
                            <th className="px-3 py-2 text-center font-bold text-gray-500">Number</th>
                            <th className="px-3 py-2 text-right font-bold text-gray-500">Overall%</th>
                            <th className="px-3 py-2 text-right font-bold text-gray-500">Gap</th>
                            <th className="px-3 py-2 text-right font-bold text-gray-500">GapR</th>
                            <th className="px-3 py-2 text-center font-bold text-gray-500">W11</th>
                            <th className="px-3 py-2 text-center font-bold text-gray-500">W21</th>
                            <th className="px-3 py-2 text-center font-bold text-gray-500">W13</th>
                            <th className="px-3 py-2 text-right font-bold text-gray-500">B/A</th>
                            <th className="px-3 py-2 text-center font-bold text-gray-500">Agree</th>
                            <th className="px-3 py-2 text-center font-bold text-rose-600">Ind</th>
                            <th className="px-3 py-2 text-right font-bold text-[#002FA7]">Consensus</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bd.top_10.map((n, idx) => (
                            <tr key={n.number} className={idx === 0 ? 'bg-indigo-50' : idx % 2 === 0 ? 'bg-gray-50/50' : ''} data-testid={`row-${n.number}`}>
                              <td className="px-3 py-2 font-bold text-gray-400">{idx + 1}</td>
                              <td className="px-3 py-2 text-center">
                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#002FA7] text-white font-bold text-sm">{n.number}</span>
                              </td>
                              <td className="px-3 py-2 text-right font-mono">{n.overall_pct}%</td>
                              <td className="px-3 py-2 text-right font-mono">{n.gap}</td>
                              <td className={`px-3 py-2 text-right font-mono font-bold ${n.gap_ratio > 1.5 ? 'text-red-600' : n.gap_ratio > 1 ? 'text-amber-600' : 'text-gray-500'}`}>{n.gap_ratio}x</td>
                              <td className="px-3 py-2 text-center">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${statusColors[n.w11_status]}`}>{n.w11_status}</span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${statusColors[n.w21_status]}`}>{n.w21_status}</span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${statusColors[n.w13_status]}`}>{n.w13_status}</span>
                              </td>
                              <td className="px-3 py-2 text-right font-mono text-green-700">{n.ba_after} ({n.ba_pct}%)</td>
                              <td className="px-3 py-2 text-center">
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${n.agreement >= 6 ? 'bg-green-100 text-green-800' : n.agreement >= 4 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-500'}`}>
                                  {n.agreement}/9
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                {n.indicators?.total > 0 ? (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-700">+{n.indicators.total}</span>
                                ) : <span className="text-gray-300">-</span>}
                              </td>
                              <td className="px-3 py-2 text-right font-bold text-lg text-[#002FA7]">{n.consensus_score || n.w3_score}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Full number list (all 50) */}
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="font-bold text-gray-900">{ballLabels[selectedBall]} — All Numbers Ranked</h3>
                    </div>
                    <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                      <table className="w-full text-xs" data-testid="all-numbers-table">
                        <thead className="sticky top-0 bg-white z-10">
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-2 py-1.5 text-left font-bold text-gray-500">Rank</th>
                            <th className="px-2 py-1.5 text-center font-bold text-gray-500">#</th>
                            <th className="px-2 py-1.5 text-right font-bold text-gray-500">%</th>
                            <th className="px-2 py-1.5 text-right font-bold text-gray-500">Gap</th>
                            <th className="px-2 py-1.5 text-right font-bold text-gray-500">GapR</th>
                            <th className="px-2 py-1.5 text-center font-bold text-gray-500">W11</th>
                            <th className="px-2 py-1.5 text-center font-bold text-gray-500">W21</th>
                            <th className="px-2 py-1.5 text-center font-bold text-gray-500">W13</th>
                            <th className="px-2 py-1.5 text-right font-bold text-gray-500">B/A</th>
                            <th className="px-2 py-1.5 text-center font-bold text-gray-500">Ag</th>
                            <th className="px-2 py-1.5 text-center font-bold text-rose-600">Ind</th>
                            <th className="px-2 py-1.5 text-right font-bold text-[#002FA7]">Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(bd.all_numbers || []).map((n, idx) => (
                            <tr key={n.number} className={idx < 10 ? 'bg-indigo-50/30' : idx % 2 === 0 ? '' : 'bg-gray-50/30'}>
                              <td className="px-2 py-1.5 font-mono text-gray-400">{idx + 1}</td>
                              <td className="px-2 py-1.5 text-center font-bold">{n.number}</td>
                              <td className="px-2 py-1.5 text-right font-mono text-gray-600">{n.overall_pct}%</td>
                              <td className="px-2 py-1.5 text-right font-mono">{n.gap}</td>
                              <td className={`px-2 py-1.5 text-right font-mono ${n.gap_ratio > 1.5 ? 'text-red-600 font-bold' : ''}`}>{n.gap_ratio}x</td>
                              <td className="px-2 py-1.5 text-center"><span className={`px-1 py-0.5 rounded text-[9px] font-bold ${statusColors[n.w11_status]}`}>{n.w11_status.charAt(0)}</span></td>
                              <td className="px-2 py-1.5 text-center"><span className={`px-1 py-0.5 rounded text-[9px] font-bold ${statusColors[n.w21_status]}`}>{n.w21_status.charAt(0)}</span></td>
                              <td className="px-2 py-1.5 text-center"><span className={`px-1 py-0.5 rounded text-[9px] font-bold ${statusColors[n.w13_status]}`}>{n.w13_status.charAt(0)}</span></td>
                              <td className="px-2 py-1.5 text-right font-mono text-green-600">{n.ba_after}</td>
                              <td className="px-2 py-1.5 text-center text-[10px] font-bold">{n.agreement}/9</td>
                              <td className="px-2 py-1.5 text-center">
                                {n.indicators?.total > 0 ? <span className="text-[10px] font-bold text-rose-600">+{n.indicators.total}</span> : <span className="text-gray-300">-</span>}
                              </td>
                              <td className="px-2 py-1.5 text-right font-mono font-bold text-[#002FA7]">{n.consensus_score || n.w3_score}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Cross-Ball Projection Summary — How many times each number appears across all 5 ball predictions */}
            {data?.balls && (
              <div className="bg-white border-2 border-indigo-300 rounded-lg overflow-hidden" data-testid="cross-ball-summary">
                <div className="p-4 border-b border-indigo-200 bg-gradient-to-r from-indigo-600 to-blue-700 text-white">
                  <h3 className="font-bold text-lg">Cross-Ball Projection Summary</h3>
                  <p className="text-[10px] text-blue-200 mt-0.5">How many times each number appears in the top 10 predictions across all 5 ball positions</p>
                </div>
                <div className="p-4">
                  {(() => {
                    // Count occurrences of each number across all 5 balls' top 10
                    const numCount = {};
                    const numBalls = {};
                    ballKeys.forEach(bk => {
                      const bd = data.balls[bk];
                      if (!bd?.top_10) return;
                      bd.top_10.forEach(n => {
                        numCount[n.number] = (numCount[n.number] || 0) + 1;
                        if (!numBalls[n.number]) numBalls[n.number] = [];
                        numBalls[n.number].push(ballLabels[bk]);
                      });
                    });
                    // Sort by count desc
                    const sorted = Object.entries(numCount)
                      .map(([num, count]) => ({ number: parseInt(num), count, balls: numBalls[parseInt(num)] || [] }))
                      .sort((a, b) => b.count - a.count || a.number - b.number);
                    const maxCount = sorted[0]?.count || 1;
                    const multiPosition = sorted.filter(s => s.count >= 2);

                    return (
                      <div className="space-y-3">
                        {/* Numbers appearing in multiple positions */}
                        {multiPosition.length > 0 && (
                          <div>
                            <h4 className="text-xs font-bold text-indigo-800 mb-2">Multi-Position Numbers (2+ balls)</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                              {multiPosition.map(s => (
                                <div key={s.number} className={`rounded-lg border-2 p-2.5 text-center ${s.count >= 4 ? 'border-red-400 bg-red-50' : s.count >= 3 ? 'border-orange-400 bg-orange-50' : 'border-amber-300 bg-amber-50'}`}>
                                  <span className={`inline-flex w-10 h-10 rounded-full items-center justify-center font-mono font-bold text-lg text-white ${s.count >= 4 ? 'bg-red-600' : s.count >= 3 ? 'bg-orange-500' : 'bg-amber-500'}`}>{s.number}</span>
                                  <p className={`text-sm font-black mt-1 ${s.count >= 4 ? 'text-red-700' : s.count >= 3 ? 'text-orange-700' : 'text-amber-700'}`}>{s.count}x</p>
                                  <p className="text-[9px] text-gray-500 mt-0.5">{s.balls.join(', ')}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Full table */}
                        <div className="overflow-x-auto">
                          <table className="w-full text-[11px]">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-2 py-1.5 text-center font-bold text-gray-500">#</th>
                                <th className="px-2 py-1.5 text-center font-bold text-gray-500">Count</th>
                                <th className="px-2 py-1.5 text-left font-bold text-gray-500">Positions</th>
                                <th className="px-2 py-1.5 font-bold text-gray-500">Bar</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sorted.map((s, idx) => (
                                <tr key={s.number} className={`${s.count >= 3 ? 'bg-red-50' : s.count >= 2 ? 'bg-amber-50/50' : idx % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                                  <td className="px-2 py-1 text-center font-bold">{s.number}</td>
                                  <td className={`px-2 py-1 text-center font-black ${s.count >= 3 ? 'text-red-700' : s.count >= 2 ? 'text-amber-700' : 'text-gray-500'}`}>{s.count}</td>
                                  <td className="px-2 py-1 text-[10px] text-gray-600">{s.balls.join(', ')}</td>
                                  <td className="px-2 py-1 w-40">
                                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                                      <div className={`h-full rounded-full ${s.count >= 3 ? 'bg-red-500' : s.count >= 2 ? 'bg-amber-400' : 'bg-blue-400'}`} style={{ width: `${(s.count / maxCount) * 100}%` }} />
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        ))}
      </main>
    </div>
  );
}
